import type { DocNode } from '@atlaskit/adf-schema/doc';

import type { ConfluencePageInitCommandProperties } from '@atlassian/rovo-content-bridge-api-commands';
import { ADFStreamer } from '@atlassian/ai-model-io/adf-streamer';
import { CommandHandler } from '@atlassian/editor-rovo-bridge/command-handler';

import { fg } from '@confluence/feature-gating';

import { log } from '../utils/aifc-logging.ts';
import { chunkSubstr, getDocument, isEmptyDocument } from '../utils/bridge-document-utils.ts';

export class PageInitCommandHandler extends CommandHandler<
	ConfluencePageInitCommandProperties,
	void
> {
	async execute(payload: ConfluencePageInitCommandProperties): Promise<void> {
		log('handle confluence-page-init-command');
		const document = await getDocument(this.props.editorAPI);
		if (!document) {
			log('PageInitCommand received but document is not available');
			return;
		}

		const isEmpty = isEmptyDocument(document as DocNode);

		log(`Received ConfluencePageInitCommand with isEmpty: ${isEmpty}`, payload?.data);

		if (!isEmpty) {
			return;
		}

		const editorSchema = this.props.editorAPI.core.sharedState.currentState()?.schema;
		if (!editorSchema) {
			log('Editor schema is not available');
			return;
		}

		log('Streaming initial ADF from ConfluencePageInitCommand', payload?.data.adf);
		this.props.onStreamStart?.({ streamType: 'init', payload });
		this.props.onStream?.({ streamType: 'init', payload });
		// In case ADF is invalid, we chunk it to retain as much of the ADF as possible
		const streamer = new ADFStreamer(editorSchema);
		const adfChunks = chunkSubstr(payload.data.adf, 50);
		for (const chunk of adfChunks) {
			log('Processing chunk from ConfluencePageInitCommand', chunk);
			streamer.processChunk(chunk);
		}
		// EDITOR-8715: ADFStreamer.sanitizeTopNode promotes `panel` -> `panel_c1` (a
		// ProseMirror-only node type) while streaming, so it can build a valid ProseMirror node
		// wherever a panel contains a table. `replaceDocument` validates the document via
		// processRawValue *before* processRawValue's own panel -> panel_c1 re-promotion runs, so a
		// `panel_c1` entity fails ADF-spec validation and gets wrapped as `unsupportedBlock`,
		// destroying the content. `getLastAdfDoc()` is the plain-ADF accessor, which lets
		// processRawValue validate a plain `panel` and re-promote afterwards — its existing correct
		// behaviour.
		const adfDoc: DocNode = streamer.getLastAdfDoc();
		log('Replacing document with ADF from ConfluencePageInitCommand', adfDoc);
		log('Replace document function - ', this.props.editorAPI.core.actions.replaceDocument);
		this.props.editorAPI.core.actions.replaceDocument(adfDoc, { scrollIntoView: false });

		// Trigger image re-uploading if handler is available
		if (fg('confluence_frontend_aifc_reupload_image')) {
			await this.props.reuploadImages?.();
		} else {
			if (adfDoc) {
				await this.props.reuploadImages?.(adfDoc);
			}
		}

		this.props.onStreamEnd?.({
			streamType: 'init',
			payload,
			adfDoc,
		});
	}
}
